import React, {useEffect, useMemo, useCallback} from "react";
import {
    Button,
    ButtonGroup,
    ClickAwayListener,
    Fade,
    Grid,
    Paper,
    Popper,
    TableCell,
    Tooltip,
    Typography,
} from "@mui/material";
import { styled } from '@mui/material/styles';
import { tableRowSx, pageContentGap } from "../../../../../theme";
import TableRow from "@mui/material/TableRow";
import CatalogCheckbox from "../../../../../components/EnhancedCheckbox";
import CatalogTableMoreOpts from "./catalog_table_more_opts";
import EnhancedSelect from "../../../../../components/EnhancedSelect";
import EnhancedErrorDialog from "../../../../../components/EnhancedErrorDialog";
import EnhancedConfirmDialog from "../../../../../components/EnhancedConfirmDialog";
import EnhancedTextField from "../../../../../components/EnhancedTextField";
import CatalogFilters, {addFiltersToTable} from "../../../../../components/CatalogFilters/catalog_filters";
import {retailerCatalogSearch, supplierCatalogSearch} from "../catalog";
import {approvalStatusOptions, deleteProduct, getAllTags, getProduct, listingStatusOptions, putProductStatus as utilUpdateStatus, putProductStatus, putProductSyncStatus, SUPPLIER_SYNDICATION_ENABLED, updateProduct, utilGetPartners, utilGetPartnerSettings, utilSearchInventory, utilSearchRetailerCatalog, getAttributes as utilGetAttributes, getAttributeSets as utilGetAttributeSets} from "../../../../../utils/utils";
import EnhancedAutocomplete from "../../../../../components/EnhancedAutocomplete";
import CatalogManagerTable from "../../../../../components/CatalogManagerTable";
import axios from "axios";
import UpdatingDialog from "../../../../../components/Dialogs/UpdatingDialog";
import ProductRejectDialog from "../../../components/Dialogs/ProductRejectDialog";
import ProductDeleteDialog from "../../../components/Dialogs/ProductDeleteDialog";
import ProductApproveDialog, {headlessApprovalFlow, compareSkus} from "../../../components/Dialogs/ProductApproveDialog";
import {PriorityHigh} from "@mui/icons-material";
import Auth from "../../../../../auth";
import Helpers from "../../../../../helpers";
import BulkApprovalActionsTable from "./bulk_approval_actions_table";
import BulkEditActions from "./bulk_edit_actions";
import { SavedSearchManager } from "../../../../../components/saved_search";
import { getTableConfig } from '../../../../../components/saved_search/utils/getConfig';
import { applySavedSearchConfig } from '../../../../../components/saved_search/utils/applyConfig';
import { createFilter } from '../../../../../components/saved_search/utils/filterManager';

// region TypeScript Interfaces
interface ErrorState {
    open: boolean;
    errorHeader: string;
    errorMessage: string;
}

interface FilterButtonProps {
    option: string;
    count: number;
    field: string;
    customFilter?: any;
    selectedValue: string;
    previousFilter: any;
    table: any;
}

interface CatalogProductFilterButtonsProps {
    table: any;
    isRetailer: boolean;
    currentCatalog: string;
    metaData?: any;
    errorCount?: number;
}

interface CatalogProductFiltersProps {
    merchantId: string;
    isRetailer: boolean;
    currentCatalog: string;
    table: any;
    refresh?: boolean;
}

interface ProductFiltersChannelsProps {
    table: any;
    retailers: any[];
}

interface ProductFiltersSyncStatusProps {
    table: any;
}

interface ProductFiltersApprovalStatusProps {
    table: any;
}

interface CatalogResultsProps {
    table: any;
    isRetailer: boolean;
    history: any;
    location: any;
    currentCatalog: string;
    config: any;
    setConfig: (fn: (prevState: any) => any) => void;
    retailers?: any[];
    forceUniqueRetailerSku?: boolean;
}
// endregion

const BULK_EDIT_ENABLED = true;

const BulkUpdateStatus = styled(Grid)(({ theme }) => ({
    flexDirection: "column",
    width: "306px",
    padding: "12px 8px",
    margin: "16px",
    background: "#FFFFFF",
    border: "1px solid #DCDCDC",
    borderRadius: "6px",
    fontSize: "14px",
    gap: pageContentGap
}));

export { BulkUpdateStatus };

const FilterButton = React.memo(function FilterButton({ option, count, field, customFilter, selectedValue, previousFilter, table }: FilterButtonProps) {
    const filter = useMemo(
        () => (customFilter !== undefined ? customFilter : createFilter(field, "eq", option)),
        [customFilter, field, option]
    );

    const handleClick = useCallback(async () => {
        if (!table || !table.extensions) return;
        if (option === "All" && previousFilter != null) {
            table.extensions.onRemoveFilter(previousFilter);
            return;
        }
        if (option === "All") return;

        if (previousFilter != null) {
            if (previousFilter.field === "errorMessage" || option === "Errors") {
                let existingFilters = table.state.filter.filters;
                let previousFilterRemoved = existingFilters.filter((f) => f !== previousFilter);
                let newFilterAdded = [...previousFilterRemoved, filter];
                table.extensions.replaceFilters(newFilterAdded);
            } else {
                table.extensions.updateFilter(filter);
            }
        } else {
            table.extensions.onAddFilter(filter);
        }
    }, [filter, option, previousFilter, table]);

    return (
        <Button
            variant='outlined'
            color={selectedValue === option ? "primary" : "default" as any}
            onClick={handleClick}
        >
            {option} ({count || 0})
        </Button>
    );
});

function CatalogProductFilterButtons(props: CatalogProductFilterButtonsProps) {
    let selectedValue = "All";
    let previousFilter = null;

    if (props.table.state.filter != null) {
        let foundError = props.table.state.filter.filters.find((f) => f.field === "errorMessage");
        if (foundError !== undefined) {
            selectedValue = "Errors";
            previousFilter = foundError;
        } else {
            if ((props.isRetailer && props.currentCatalog === "partnercatalog") || (!props.isRetailer && props.currentCatalog === "mycatalog")) {
                let foundApproval = props.table.state.filter.filters.find((f) => f.field === "approvalStatus");
                if (foundApproval !== undefined) {
                    selectedValue = foundApproval.value;
                    previousFilter = foundApproval;
                }
            } else {
                let foundSync = props.table.state.filter.filters.find((f) => f.field === "syncStatus");
                if (foundSync !== undefined) {
                    selectedValue = foundSync.value;
                    previousFilter = foundSync;
                }
            }
        }
    }

    let statusButtons: React.ReactNode = <></>

    if ((props.isRetailer && props.currentCatalog === "partnercatalog") || (!props.isRetailer && props.currentCatalog === "mycatalog")) {
        statusButtons = approvalStatusOptions.map((option, index) => {
            return (
                <FilterButton
                    key={index}
                    option={option}
                    field={"approvalStatus"}
                    count={props.metaData[option]}
                    selectedValue={selectedValue}
                    previousFilter={previousFilter}
                    table={props.table}
                />
            )
        });
    } else {
        statusButtons = listingStatusOptions.map((option, index) => {
            return (
                <FilterButton
                    key={index}
                    option={option}
                    field={"syncStatus"}
                    count={props.metaData[option]}
                    selectedValue={selectedValue}
                    previousFilter={previousFilter}
                    table={props.table}
                />
            )
        });
    }

    return (
        <>
            {statusButtons}

            {/*Non Status Filter Buttons*/}
            <FilterButton
                option={"Errors"}
                field={"errorMessage"}
                count={props.errorCount || 0}
                customFilter={createFilter("errorMessage", "ne", null)}
                selectedValue={selectedValue}
                previousFilter={previousFilter}
                table={props.table}
            />
        </>
    );
}

function CatalogProductFilters(props: CatalogProductFiltersProps) {
    const {merchantId, isRetailer} = props;
    const [metaData, setMetaData] = React.useState<Record<string, number>>({});
    const [errorCount, setErrorCount] = React.useState<number>(0);

    useEffect(() => {
        // Gather counts for all statuses
        const getMetaData = async () => {
            let counts: Record<string, number> = {};
            let url = "";
            if (isRetailer) {
                url = retailerCatalogSearch;
                if (props.currentCatalog === "partnercatalog")
                    url = supplierCatalogSearch;
            } else {
                url = supplierCatalogSearch;
                if (props.currentCatalog === "partnercatalog" && merchantId)
                    url = retailerCatalogSearch + "?merchantId=" + merchantId;
            }

            let status = "";
            if ((props.isRetailer && props.currentCatalog === "partnercatalog") || (!props.isRetailer && props.currentCatalog === "mycatalog")) {
                status = "approvalStatus";
            } else {
                status = "syncStatus";
            }

            url = url + `${props.currentCatalog === "partnercatalog" && !props.isRetailer ? "&" : "?"}$top=0&$count=true&facets=['${status}']`;

            axios.get(url).then((response) => {
                counts["All"] = response.data["@odata.count"];
                response.data["facets"][status].forEach((value) => {
                    counts[value.value] = value.count;
                });
                setMetaData(counts);
            });
        }

        const getErrorData = async () => {
            var count = 0;
            let url = "";
            if (isRetailer) {
                url = retailerCatalogSearch;
                if (props.currentCatalog === "partnercatalog")
                    url = supplierCatalogSearch;
            } else {
                url = supplierCatalogSearch;
                if (props.currentCatalog === "partnercatalog" && merchantId)
                    url = retailerCatalogSearch + "?merchantId=" + merchantId;
            }

            url = url + `${props.currentCatalog === "partnercatalog" && !props.isRetailer ? "&" : "?"}$top=0&$count=true&facets=['errorMessage']`;

            axios.get(url).then((response) => {
                response.data["facets"]["errorMessage"].forEach((value) => {
                    count += value.count;
                });
                setErrorCount(count);
            });
        }

        getMetaData();
        getErrorData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.currentCatalog, props.refresh, props.merchantId]);

    return (
        <Grid item container style={{gap: "8px", flexWrap: "wrap", flex: 1, minWidth: 0}}>
            <CatalogProductFilterButtons
                currentCatalog={props.currentCatalog}
                isRetailer={props.isRetailer}
                table={props.table}
                metaData={metaData}
                errorCount={errorCount}
            />
        </Grid>
    )
}

function ProductFiltersChannels(props: ProductFiltersChannelsProps) {
    const {retailers} = props;
    // const [metadata, setMetadata] = React.useState({});

    const currentFilters = props.table.state.filter?.filters ?? [];
    const values = currentFilters
        .filter((filter) => filter.field === "merchantName")
        .map((filter) => {
            const foundRetailer = retailers.find((retailer) => retailer.CompanyName === filter.value);
            return {
                label: foundRetailer?.CompanyName ?? filter.value,
                value: filter.value
            }
        });

    const addFiltersToTable = (retailerNameArray: string[]) => {
        const currentFilters = props.table.state.filter?.filters ?? [];
        const newFilters = currentFilters.filter((filter: any) => filter.field !== "merchantName");

        const newRetailerNameFilters = retailerNameArray.map((retailerName: string) => ({
            field: "merchantName",
            operator: "eq",
            value: retailerName
        }));
        newFilters.push(...newRetailerNameFilters);

        props.table.extensions.replaceFilters(newFilters);
    }

    const removeAllFilters = () => {
        const currentFilters = props.table.state.filter?.filters ?? [];
        const newFilters = currentFilters.filter((filter: any) => filter.field !== "merchantName");
        props.table.extensions.replaceFilters(newFilters);
    }

    // useEffect(() => {
    //     const getRetailerData = () => {
    //         const counts = {};
    //         const url = supplierCatalogSearch + "?$top=0&$count=true&facets=['merchantName']";
    //         axios.get(url).then((response) => {
    //             counts["All"] = response.data["@odata.count"];
    //             response.data["facets"]["merchantName"].forEach((value) => {
    //                 counts[value.value] = value.count;
    //             });
    //
    //             setMetadata(counts);
    //         });
    //     }
    //
    //     getRetailerData();
    // }, []);

    return (
        <EnhancedSelect
            isMulti={true}
            isClearable={true}
            placeholder={"Channel"}
            value={values}
            options={
                [
                    ...retailers.map((retailer) => ({
                        label: retailer.CompanyName,
                        value: retailer.CompanyName
                    }))
                ]
            }
            onChange={(e) => {
                if(e == null)
                    removeAllFilters();
                else if (e.value === "All")
                    removeAllFilters();
                else
                    addFiltersToTable(e.map((retailer) => retailer.value));
            }}

            valueContainerStyle={{
                overflowY: "auto",
                height: "36px"
            }}

            chipStyles={{
                minWidth: "100px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis"
            }}
        />
    )
}

function ProductFiltersSyncStatus(props: ProductFiltersSyncStatusProps) {
    // const [metadata, setMetadata] = React.useState({});
    const currentFilters = props.table.state.filter?.filters ?? [];
    const values = currentFilters
        .filter((filter) => filter.field === "syncStatus")
        .map((filter) => {
            return {
                label: filter.value,
                value: filter.value
            }
        });

    const addFiltersToTable = (status: string) => {
        const currentFilters = props.table.state.filter?.filters ?? [];

        // Check if sync status filter already exists
        // If it replace it, else add it
        let syncStatusFilter = currentFilters.find((filter: any) => filter.field === "syncStatus");
        if (syncStatusFilter) {
            syncStatusFilter.value = status;
        }
        else {
            syncStatusFilter = createFilter("syncStatus", "eq", status)
            currentFilters.push(syncStatusFilter);
        }

        props.table.extensions.replaceFilters(currentFilters);
    }

    const removeAllFilters = () => {
        const currentFilters = props.table.state.filter?.filters ?? [];
        const newFilters = currentFilters.filter((filter: any) => filter.field !== "syncStatus");
        props.table.extensions.replaceFilters(newFilters);
    }

    // useEffect(() => {
    //     const getSyncStatusData = () => {
    //         const counts = {};
    //         const url = supplierCatalogSearch + "?$top=0&$count=true&facets=['syncStatus']";
    //         axios.get(url).then((response) => {
    //             counts["All"] = response.data["@odata.count"];
    //             response.data["facets"]["syncStatus"].forEach((value) => {
    //                 counts[value.value] = value.count;
    //             });
    //
    //             setMetadata(counts);
    //         });
    //     }
    //
    //     getSyncStatusData();
    // }, []);

    return (
        <EnhancedSelect
            isClearable={true}
            placeholder={"Listing Status"}
            value={values}
            options={
                listingStatusOptions.map((option) => ({
                    label: option,
                    value: option
                })).filter((option) => option.value !== "All")
            }
            onChange={(e) => {
                if(e == null)
                    removeAllFilters();
                else if (e.value === "All")
                    removeAllFilters();
                else
                    addFiltersToTable(e.value);
            }}
            containerStyle={{width: "100%"}}
            placeholderStyle={{
                minWidth: "100px",
                overflow: "hidden",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis"
            }}
        />
    )
}

function ProductFiltersApprovalStatus(props: ProductFiltersApprovalStatusProps) {
    // const [metadata, setMetadata] = React.useState({});
    const currentFilters = props.table.state.filter?.filters ?? [];
    const values = currentFilters
        .filter((filter) => filter.field === "approvalStatus")
        .map((filter) => {
            return {
                label: filter.value,
                value: filter.value
            }
        });

    const addFiltersToTable = (status: string) => {
        const currentFilters = props.table.state.filter?.filters ?? [];

        // Check if sync status filter already exists
        // If it replace it, else add it
        let approvalStatusFilter = currentFilters.find((filter: any) => filter.field === "approvalStatus");
        if (approvalStatusFilter) {
            approvalStatusFilter.value = status;
        }
        else {
            approvalStatusFilter = createFilter("approvalStatus", "eq", status)
            currentFilters.push(approvalStatusFilter);
        }

        props.table.extensions.replaceFilters(currentFilters);
    }

    const removeAllFilters = () => {
        const currentFilters = props.table.state.filter?.filters ?? [];
        const newFilters = currentFilters.filter((filter: any) => filter.field !== "approvalStatus");
        props.table.extensions.replaceFilters(newFilters);
    }

    // useEffect(() => {
    //     const getSyncStatusData = () => {
    //         const counts = {};
    //         const url = supplierCatalogSearch + "?$top=0&$count=true&facets=['approvalStatus']";
    //         axios.get(url).then((response) => {
    //             counts["All"] = response.data["@odata.count"];
    //             response.data["facets"]["approvalStatus"].forEach((value) => {
    //                 counts[value.value] = value.count;
    //             });
    //
    //             setMetadata(counts);
    //         });
    //     }
    //
    //     getSyncStatusData();
    // }, []);

    return (
        <EnhancedSelect
            isClearable={true}
            placeholder={"Approval Status"}
            value={values}
            options={
                approvalStatusOptions.map((option) => ({
                    label: option,
                    value: option
                })).filter((option) => option.value !== "All")
            }
            onChange={(e) => {
                if(e == null)
                    removeAllFilters();
                else if (e.value === "All")
                    removeAllFilters();
                else
                    addFiltersToTable(e.value);
            }}
            containerStyle={{width: "100%"}}
            placeholderStyle={{
                minWidth: "100px",
                overflow: "hidden",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis"
            }}
        />
    )
}

// Helper function to extract table configuration for saved searches

function CatalogResults(props: CatalogResultsProps) {
    let table = props.table;
    const {currentCatalog} = props;
    const isMarketplace = props.retailers && props.retailers.some(retailer => retailer.Marketplace === true);

    let merchantId = new Helpers().getUrlObject('merchantId') || "";
    const hasInventoryManagePermission = Auth.hasPermission("inventory/manage");

    // LB-449 FIX 3: BulkEditActions.loadMetadata scopes attribute/category metadata by
    // the first selected product only. For suppliers that metadata is merchantId-scoped,
    // so a selection spanning multiple merchantIds would silently load metadata for just
    // the first product's merchant. Gate the bulk Edit action when a supplier's selection
    // spans more than one distinct merchantId (retailers are single-tenant here and unaffected).
    const bulkEditSelectedProducts = (table.data || []).filter(
        (product) => props.table.state.selected.includes(product.id)
    );
    const bulkEditSpansMultipleMerchants = !props.isRetailer
        && new Set(bulkEditSelectedProducts.map((product) => product.merchantId)).size > 1;

    const [rejectOptionalNote, setRejectOptionalNote] = React.useState<string>("");
    const [updateStatusValue, setUpdateStatusValue] = React.useState<any>(null);

    const [bulkTagTags, setBulkTagTags] = React.useState<string[]>([]);
    const [bulkUntagTags, setBulkUntagTags] = React.useState<string[]>([]);
    const [avalTags, setAvalTags] = React.useState<string[]>([]);

    const [updateStatusesAnchorEl, setUpdateStatusesAnchorEl] = React.useState<HTMLElement | null>(null);
    const [updateStatusesOpen, setUpdateStatusesOpen] = React.useState<boolean>(false);
    const [bulkTagAnchorEl, setBulkTagAnchorEl] = React.useState<HTMLElement | null>(null);
    const [bulkTagOpen, setBulkTagOpen] = React.useState<boolean>(false);
    const [bulkUntagAnchorEl, setBulkUntagAnchorEl] = React.useState<HTMLElement | null>(null);
    const [bulkUntagOpen, setBulkUntagOpen] = React.useState<boolean>(false);
    const [bulkDeleteConfirm, setBulkDeleteConfirm] = React.useState<boolean>(false);
    const [bulkEditOpen, setBulkEditOpen] = React.useState<boolean>(false);

    const [bulkRejectAnchorEl, setBulkRejectAnchorEl] = React.useState<HTMLElement | null>(null);
    const [bulkRejectConfirm, setBulkRejectConfirm] = React.useState<boolean>(false);

    const [bulkApproveManualReviews, setBulkApproveManualReviews] = React.useState<any[]>([]);
    const [bulkApproveManualReviewsOpen, setBulkApproveManualReviewsOpen] = React.useState<boolean>(false);
    const [bulkApproveManualReviewApproveAndList, setBulkApproveManualReviewApproveAndList] = React.useState<boolean>(false);

    //region Row Interaction
    const [approveDialogOpen, setApproveDialogOpen] = React.useState<boolean>(false);
    const [rejectDialogOpen, setRejectDialogOpen] = React.useState<boolean>(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = React.useState<boolean>(false);
    const [selectedRow, setSelectedRow] = React.useState<any>(null);
    //endregion

    const [updating, setUpdating] = React.useState<boolean>(false);
    const [updatingProgress, setUpdatingProgress] = React.useState<number>(0);
    const [targetProgress, setTargetProgress] = React.useState<number>(0);
    const [isBulkApproving, setIsBulkApproving] = React.useState<boolean>(false);
    const [error, setError] = React.useState<ErrorState>({open: false, errorHeader: "", errorMessage: ""});

    //region Supplier Specific State //
    const [selectedPartnerId, setSelectedPartnerId] = React.useState<string>(merchantId);
    const [availablePartners, setAvailablePartners] = React.useState<any[]>([]);
    //endregion

    // Format CatalogManagerTable Data Into Rows //
    const generateRows = () => {
        return table.data.map((row, rowIndex) => {
            let isSelectable = hasInventoryManagePermission
                && (currentCatalog === "mycatalog" || (currentCatalog === "partnercatalog" && props.isRetailer));

            return (
                <TableRow key={rowIndex} sx={tableRowSx} style={{height: "70px", cursor: "pointer"}}
                          data-testid={`poc-catalog-row-${row.sku}`}
                          onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();

                              let partnerId = "";
                              if (props.isRetailer)
                                  partnerId = currentCatalog === "partnercatalog" ? `&partnerId=${row.supplierId}` : "";
                              else
                                  partnerId = `&partnerId=${row.merchantId}`;

                              // Encoding the URL twice is necessary to prevent the URL from being decoded by the browser
                              let encodedProductId = encodeURIComponent(encodeURIComponent(row.sku));
                              props.history.push(`/productonboardingcenter/product?catalog=${currentCatalog}&productId=${encodedProductId}` + partnerId)
                          }}
                >
                    {isSelectable && (
                            <TableCell padding="checkbox" style={{width: '3em'}}>
                                <CatalogCheckbox checked={props.table.extensions.isSelected(row.id)}
                                                 onChange={(e) => props.table.extensions.handleClick(e, row.id)}
                                                 onClick={(e) => e.stopPropagation()}
                                />
                            </TableCell>
                        )}
                    {props.config.columns.map((column, colIndex) => {
                        if (props.table.extensions.isColumnHidden(column)) return null;
                        if (column.hidden && !column.toggleable) return null;

                        let content = column.template
                            ? (
                                <TableCell key={`${column.id}-${rowIndex}`}
                                           style={{
                                               width: column.width,
                                               maxWidth: column.width,
                                               textOverflow: "ellipsis",
                                               overflow: "hidden",
                                               padding: !isSelectable ? undefined : "0 6px 0 0",
                                           }}>
                                    {column.template(row, column, colIndex, rowIndex, (filter) =>
                                        addFiltersToTable([filter], props.table))}
                                </TableCell>
                            )
                            : <TableCell key={`${rowIndex}-${colIndex}`}>{row[column.id]}</TableCell>;
                        return <React.Fragment key={`${rowIndex}-${colIndex}`}>{content}</React.Fragment>
                    })}
                    <TableCell style={{width: 0, padding: "4px 0"}}
                               onClick={(e) => {
                                   e.stopPropagation();
                                   e.preventDefault();
                               }}
                    >
                        <Grid container style={{flexDirection: "column", margin: "0"}}>
                            <CatalogTableMoreOpts
                                isRetailer={props.isRetailer} merchantId={merchantId}
                                table={props.table} row={row} currentCatalog={currentCatalog}
                                history={props.history} location={props.location}
                                config={props.config} setConfig={props.setConfig}
                                setUpdating={setUpdating} setError={setError}
                                setApproveDialogOpen={setApproveDialogOpen}
                                setRejectDialogOpen={setRejectDialogOpen}
                                setDeleteDialogOpen={setDeleteDialogOpen}
                                setSelectedRow={setSelectedRow} updateListing={updateListing}
                            />
                        </Grid>
                    </TableCell>
                    <TableCell style={{width: 0, padding: "4px 20px 4px 0"}}>
                        {row.errorMessage && (
                            <Grid container justifyContent={"center"} alignItems={"center"}>
                                <Tooltip title={"This product has an error, please open to view."}>
                                    <PriorityHigh style={{color: "#f44336"}}/>
                                </Tooltip>
                            </Grid>
                        )}
                    </TableCell>
                </TableRow>
            );
        });
    };

    //region Functions
    const bulkUpdateStatus = async (status: string) => {
        if (!hasInventoryManagePermission) {
            setError({
                open: true,
                errorHeader: "Permission Denied",
                errorMessage: "You do not have permission to perform this action."
            })
            return;
        }

        setUpdating(true);

        let isSupplier = currentCatalog === "partnercatalog" || !props.isRetailer;
        let selectedProducts = table.data.filter((product, index) => props.table.state.selected.includes(product.id));
        let errors = [];

        await Promise.all(selectedProducts.map(async (product) => {
            let partnerId = !props.isRetailer ? product.merchantId : product.supplierId;
            let runStatus = null;

            if(props.isRetailer)
                runStatus = await putProductStatus(product.sku, partnerId, status, isSupplier, rejectOptionalNote);
            else
                runStatus = await (putProductSyncStatus as any)(product.sku, partnerId, status, isSupplier, rejectOptionalNote);

            if (runStatus !== true)
                errors.push({id: product.sku, error: runStatus});
        }));

        if (errors.length > 0)
            setError({
                open: true,
                errorHeader: "The following products failed to update!",
                errorMessage: errors.map((error) => `${error.id} - ${error.error.errorMessage}`).join("\n")
            });

        await new Promise(r => setTimeout(r, 750));
        props.setConfig((prevState) => ({...prevState, refresh: !props.config.refresh}));
        setUpdating(false);
        setUpdatingProgress(0);
    }
    const bulkApprove = async (andList: boolean) => {
        setUpdating(true);
        setUpdatingProgress(0);
        setTargetProgress(0);
        setIsBulkApproving(true);

        let selectedProducts: any[] = table.data.filter((product: any, index: number) => props.table.state.selected.includes(product.id));
        const manualReviewProducts: any[] = [];
        const errors: { id: string; error: { errorMessage: string } }[] = [];

        // Check if selected products are already approved
        const approvedProducts = selectedProducts.filter((product: any) => product.approvalStatus === "Approved");
        errors.push(...approvedProducts.map((product: any) => ({id: product.sku, error: {errorMessage: "Product is already approved."}})));
        selectedProducts = selectedProducts.filter((product: any) => product.approvalStatus !== "Approved");

        // Get unique supplier IDs and fetch their partner settings
        const uniqueSupplierIds = Array.from(new Set(selectedProducts.map((product: any) => product.supplierId)));
        const partnerSettingsMap: Record<number, any> = {};

        // Start progress animation for data fetching (0-5%)
        // Extra promise waits below are to ensure a smooth animation
        setTargetProgress(2);

        await Promise.all(uniqueSupplierIds.map(async (supplierId) => {
            try {
                const settings = await utilGetPartnerSettings(supplierId);
                partnerSettingsMap[supplierId] = settings;
            } catch (error) {
                console.error(`Failed to get partner settings for supplier ${supplierId}:`, error);
                partnerSettingsMap[supplierId] = { caseSensitive: false, autoMatch: false };
            }
        }));

        setTargetProgress(5);


        // Get all required check data for selected products
        // For case-insensitive partners, uppercase SKUs for search
        const allParentSkus = selectedProducts.map((product) => {
            if (!product.parentSku) return null;
            const settings = partnerSettingsMap[product.supplierId] || { caseSensitive: false };
            return settings.caseSensitive ? product.parentSku : product.parentSku.toUpperCase();
        }).filter((parentSku) => parentSku);

        const allProductSkus = selectedProducts.map((product) => {
            if (!product.sku) return null;
            const settings = partnerSettingsMap[product.supplierId] || { caseSensitive: false };
            return settings.caseSensitive ? product.sku : product.sku.toUpperCase();
        }).filter((sku) => sku);

        let foundParentSkuMappings: any = {};
        if(allParentSkus.length > 0) {
            foundParentSkuMappings = await utilSearchInventory(allParentSkus);
            setTargetProgress(8);
        }

        let foundSkuMappings: any = {};
        if(allProductSkus.length > 0) {
            foundSkuMappings = await utilSearchInventory(allProductSkus);
            setTargetProgress(12);
        }

        let foundRetailerProductsWithParentSku: any = {};
        if(allParentSkus.length > 0) {
            foundRetailerProductsWithParentSku = await utilSearchRetailerCatalog(`$filter=(${allParentSkus.map((parentSku) => `(parentSku eq '${encodeURIComponent((parentSku || "").replaceAll("'","''"))}')`).join(" or ")})`);
            setTargetProgress(16);
        }

        let foundRetailerProductsWithSku: any = {};
        if(allProductSkus.length > 0) {
            foundRetailerProductsWithSku = await utilSearchRetailerCatalog(`$filter=(${allProductSkus.map((sku) => `(sku eq '${encodeURIComponent((sku || "").replaceAll("'","''"))}')`).join(" or ")})`);
            setTargetProgress(20);
        }

        // If there is a merchant SKU mapping in inventory check for a product, get the mapped product
        let foundRetailerProductsWithMappedSku: any = {};
        let mappedSkus: string[] = [];
        if(foundSkuMappings.data) {
            foundSkuMappings.data.forEach((mapping) => {
                // Get partner settings for this mapping
                const partnerSettings = partnerSettingsMap[mapping.supplierId] || { caseSensitive: false };
                if(mapping.merchantSku && !compareSkus(mapping.merchantSku, mapping.supplierSku, partnerSettings.caseSensitive)) {
                    // Uppercase the merchantSku if case sensitivity is off
                    const skuToAdd = partnerSettings.caseSensitive ? mapping.merchantSku : mapping.merchantSku.toUpperCase();
                    mappedSkus.push(skuToAdd);
                }
            });

            if(mappedSkus.length > 0) {
                foundRetailerProductsWithMappedSku = await utilSearchRetailerCatalog(`$filter=(${mappedSkus.map((sku) => `(sku eq '${encodeURIComponent((sku || "").replaceAll("'","''"))}')`).join(" or ")})`);
                setTargetProgress(25);
            }
        }

        // Set progress to 25% after all data fetching is complete
        setTargetProgress(25);

        if(foundParentSkuMappings.error || foundRetailerProductsWithParentSku.error || foundRetailerProductsWithParentSku.error || foundRetailerProductsWithSku.error || foundRetailerProductsWithMappedSku.error) {
            setTargetProgress(100);
            await new Promise(r => setTimeout(r, 500));

            setError({
                open: true,
                errorHeader: "Error",
                errorMessage: "Error approving products. Please try again later."
            });
            setUpdating(false);

            await new Promise(r => setTimeout(r, 100));
            setUpdatingProgress(0);
            setTargetProgress(0);
            setIsBulkApproving(false);
            return;
        }

        // Track SKUs that have been successfully created during this bulk approval
        const approvedSkus: Map<string, any> = new Map(); // Map of SKU -> { product, supplierId, supplierName, timestamp }

        // Fetched once for the whole run: the rule set is the retailer's and does not vary by
        // product, and a per-product fetch would add one request per selected row.
        const supplierIds = Array.from(new Set(selectedProducts.map((p: any) => String(p.supplierId))));
        const [ruleAttributes, ...supplierAttributeSets] = await Promise.all([
            utilGetAttributes(0, true),
            ...supplierIds.map((id) => utilGetAttributeSets(true, id, null, true))
        ]);
        if (!Array.isArray(ruleAttributes) || supplierAttributeSets.some((sets) => !Array.isArray(sets))) {
            setError({
                open: true,
                errorHeader: "Error",
                errorMessage: "An error occurred while trying to load the catalog validation rules."
            });
            setUpdating(false);
            await new Promise(r => setTimeout(r, 100));
            setUpdatingProgress(0);
            setTargetProgress(0);
            setIsBulkApproving(false);
            return;
        }
        const attributeSetsBySupplier: Record<string, any[]> = {};
        supplierIds.forEach((id, index) => { attributeSetsBySupplier[id] = supplierAttributeSets[index]; });
        const retailerRules = {attributes: ruleAttributes, attributeSetsBySupplier};

        // Process products sequentially to detect conflicts in real-time
        let processedCount = 0;
        const totalCount = selectedProducts.length;

        for (const product of selectedProducts) {
            const effectiveSku = product.sku;
            const partnerSettings = partnerSettingsMap[product.supplierId] || { caseSensitive: false };
            const compareSku = partnerSettings.caseSensitive ? effectiveSku : effectiveSku.toUpperCase();

            // Check if this SKU conflicts with a product we just approved in this batch
            if (!props.forceUniqueRetailerSku && approvedSkus.has(compareSku)) {
                const approvedProduct = approvedSkus.get(compareSku);
                // If different suppliers have the same SKU, flag for manual review
                if (approvedProduct.supplierId !== product.supplierId) {
                    const conflictMessage = `SKU "${effectiveSku}" conflicts with a product from ${approvedProduct.supplierName} that was just approved. Manual review required.`;

                    // Store conflict information for the manual review dialog
                    product.conflictInfo = {
                        conflictingSupplier: approvedProduct.supplierName,
                        conflictingSupplierId: approvedProduct.supplierId,
                        conflictingProduct: approvedProduct.product,
                        approvedAt: approvedProduct.timestamp,
                        message: conflictMessage
                    };

                    manualReviewProducts.push(product);

                    processedCount++;
                    const progressPercentage = Math.round(25 + (processedCount / totalCount) * 75);
                    setTargetProgress(progressPercentage);

                    continue; // Skip
                }
            }

            // Check if this SKU already exists in the retailer catalog
            let retailerProduct = null;
            if(foundRetailerProductsWithSku.data) {
                retailerProduct = foundRetailerProductsWithSku.data.find((rp) =>
                    compareSkus(rp.sku, effectiveSku, partnerSettings.caseSensitive)
                );
            }

            let parentSkuMapping = null;
            if(foundParentSkuMappings.data)
                parentSkuMapping = foundParentSkuMappings.data.find((mapping) => compareSkus(mapping.supplierSku, product.parentSku, partnerSettings.caseSensitive) && mapping.supplierId === product.supplierId);

            let retailerProductParentSkuMappings = null;
            if(foundRetailerProductsWithParentSku.data)
                retailerProductParentSkuMappings =  foundRetailerProductsWithParentSku.data.find((retailerProduct) => compareSkus(retailerProduct.parentSku, product.parentSku, partnerSettings.caseSensitive));

            let productInventory = null;
            if(foundSkuMappings.data)
                productInventory = foundSkuMappings.data.find((mapping) => compareSkus(mapping.supplierSku, product.sku, partnerSettings.caseSensitive) && mapping.supplierId === product.supplierId);

            let retailerProductMappedSku = null;
            if(foundRetailerProductsWithMappedSku.data && productInventory?.merchantSku)
                retailerProductMappedSku = foundRetailerProductsWithMappedSku.data.find((retailerProduct) =>
                    compareSkus(retailerProduct.sku, productInventory.merchantSku, partnerSettings.caseSensitive)
                );

            let runStatus = await headlessApprovalFlow(product, retailerProduct, productInventory, retailerProductParentSkuMappings, parentSkuMapping, retailerProductMappedSku, andList, retailerRules, props.forceUniqueRetailerSku || false);

            processedCount++;
            const progressPercentage = Math.round(25 + (processedCount / totalCount) * 75);
            setTargetProgress(progressPercentage);

            if (runStatus.success !== true) {
                errors.push({id: product.sku, error: {errorMessage: runStatus.error}});
            } else if (runStatus.data === "manual_review") {
                manualReviewProducts.push(product);
            } else {
                // Successfully approved - track this SKU to detect future conflicts
                if (!retailerProduct) {
                    // This is a new product that was just created
                    approvedSkus.set(compareSku, {
                        product,
                        supplierId: product.supplierId,
                        supplierName: product.supplierName,
                        timestamp: new Date()
                    });

                    // Update foundRetailerProductsWithSku to include this newly created product
                    // This ensures subsequent products in the batch see this as an existing product
                    if (!foundRetailerProductsWithSku.data) {
                        foundRetailerProductsWithSku.data = [];
                    }
                    foundRetailerProductsWithSku.data.push({
                        sku: effectiveSku,
                        supplierId: product.supplierId,
                        supplierName: product.supplierName
                    });

                    // Also update parent SKU tracking so subsequent products in the batch
                    // detect parent SKU conflicts from products just approved
                    if (product.parentSku) {
                        if (!foundRetailerProductsWithParentSku.data) {
                            foundRetailerProductsWithParentSku.data = [];
                        }
                        foundRetailerProductsWithParentSku.data.push({
                            parentSku: product.parentSku,
                            sku: effectiveSku,
                            supplierId: product.supplierId,
                            supplierName: product.supplierName
                        });
                    }
                }
            }
        }

        if (errors.length > 0)
            setError({
                open: true,
                errorHeader: "The following products had failures approving!",
                errorMessage: errors.map((error) => `${error.id} - ${error.error.errorMessage}`).join("\n")
            });

        setTargetProgress(100);

        await new Promise(r => setTimeout(r, 1500));
        setUpdating(false);
        await new Promise(r => setTimeout(r, 100));

        setUpdatingProgress(0);
        setTargetProgress(0);
        setIsBulkApproving(false);

        await new Promise(r => setTimeout(r, 100));

        if (manualReviewProducts.length > 0){
            setBulkApproveManualReviews(manualReviewProducts);
            setBulkApproveManualReviewsOpen(true);
            setBulkApproveManualReviewApproveAndList(andList);
        }
        else {
            props.setConfig((prevState) => ({...prevState, refresh: !props.config.refresh}));
        }
    }
    const bulkDelete = async () => {
        if (!hasInventoryManagePermission) {
            setError({
                open: true,
                errorHeader: "Permission Denied",
                errorMessage: "You do not have permission to perform this action."
            })
            return;
        }

        setUpdating(true);

        let isPartner = currentCatalog === "partnercatalog";
        let selectedProducts = table.data.filter((product, index) => props.table.state.selected.includes(product.id));
        let errors = [];
        await Promise.all(selectedProducts.map(async (product) => {
            let partnerId = props.isRetailer ? product.supplierId : product.merchantId;
            let runStatus: any = await deleteProduct(product.sku, partnerId, isPartner, props.isRetailer);
            if (runStatus.errorMessage)
                errors.push({id: product.sku, error: runStatus});
        }));

        if (errors.length > 0)
            setError({
                open: true,
                errorHeader: "The following products failed to delete!",
                errorMessage: errors.map((error) => `${error.id} - ${error.error.errorMessage}`).join("\n")
            });

        await new Promise(r => setTimeout(r, 750));
        props.setConfig((prevState) => ({...prevState, refresh: !props.config.refresh}));
        setUpdating(false);
        setUpdatingProgress(0);
    }
    const bulkTag = async () => {
        if (!hasInventoryManagePermission) {
            setError({
                open: true,
                errorHeader: "Permission Denied",
                errorMessage: "You do not have permission to perform this action."
            })
            return;
        }

        setUpdating(true);

        let isPartner = currentCatalog === "partnercatalog";
        let selectedProducts = table.data.filter((product, index) => props.table.state.selected.includes(product.id));
        let errors = [];
        await Promise.all(selectedProducts.map(async (product) => {
            let partnerId = props.isRetailer ? product.supplierId : product.merchantId;
            let rowItem = await getProduct(product.sku, partnerId, isPartner, props.isRetailer);
            if (rowItem.errorMessage)
                errors.push({id: product.sku, error: rowItem});
            else {
                let productTags = rowItem.Attributes.tags == null ? [] : rowItem.Attributes.tags;
                let updatedTags = [...productTags, ...bulkTagTags];
                let uniqueTags = Array.from(new Set(updatedTags));
                rowItem.Attributes.tags = uniqueTags;
                let runStatus = await updateProduct(rowItem, product.sku, partnerId, isPartner, props.isRetailer);
                if (runStatus.errorMessage)
                    errors.push({id: product.sku, error: runStatus});
            }
        }));

        if (errors.length > 0)
            setError({
                open: true,
                errorHeader: "The following products failed to tag!",
                errorMessage: errors.map((error) => `${error.id} - ${error.error.errorMessage}`).join("\n")
            });

        await new Promise(r => setTimeout(r, 750));
        props.setConfig((prevState) => ({...prevState, refresh: !props.config.refresh}));
        setUpdating(false);
        setUpdatingProgress(0);
    }
    const bulkUntag = async () => {
        if (!hasInventoryManagePermission) {
            setError({
                open: true,
                errorHeader: "Permission Denied",
                errorMessage: "You do not have permission to perform this action."
            })
            return;
        }

        setUpdating(true);

        let isPartner = currentCatalog === "partnercatalog";
        let selectedProducts = table.data.filter((product, index) => props.table.state.selected.includes(product.id));
        let errors = [];
        await Promise.all(selectedProducts.map(async (product) => {
            let partnerId = props.isRetailer ? product.supplierId : product.merchantId;
            let rowItem = await getProduct(product.sku, partnerId, isPartner, props.isRetailer);
            if (rowItem.errorMessage)
                errors.push({id: product.sku, error: rowItem});
            else {
                let productTags = rowItem.Attributes.tags == null ? [] : rowItem.Attributes.tags;
                let updatedTags = productTags.filter(tag => !bulkUntagTags.includes(tag));
                rowItem.Attributes.tags = updatedTags;
                let runStatus = await updateProduct(rowItem, product.sku, partnerId, isPartner, props.isRetailer);
                if (runStatus.errorMessage)
                    errors.push({id: product.sku, error: runStatus});
            }
        }));

        if (errors.length > 0)
            setError({
                open: true,
                errorHeader: "The following products failed to untag!",
                errorMessage: errors.map((error) => `${error.id} - ${error.error.errorMessage}`).join("\n")
            });

        await new Promise(r => setTimeout(r, 750));
        props.setConfig((prevState) => ({...prevState, refresh: !props.config.refresh}));
        setUpdating(false);
        setUpdatingProgress(0);
    }
    const updateListing = async (row: any, listStatus: string) => {
        if (!hasInventoryManagePermission) {
            setError({
                open: true,
                errorHeader: "Permission Denied",
                errorMessage: "You do not have permission to perform this action."
            })
            return;
        }

        if (row.syncStatus === listStatus) return

        setUpdating(true);
        let runStatus = await utilUpdateStatus(row.sku, row.supplierId, listStatus, false);
        if (runStatus !== true) setError(runStatus);
        await new Promise(r => setTimeout(r, 750));
        props.setConfig((prevState) => ({...prevState, refresh: !props.config.refresh}));
        setUpdating(false);
        setUpdatingProgress(0);
    }
    //endregion

    //region Lifecycle
    // Smooth progress animation effect //
    useEffect(() => {
        if (!isBulkApproving) return;

        const animationInterval = setInterval(() => {
            setUpdatingProgress(currentProgress => {
                if (currentProgress >= targetProgress) {
                    return targetProgress;
                }

                const difference = targetProgress - currentProgress;
                const increment = Math.max(0.2, Math.min(1, difference * 0.1));

                const newProgress = Math.min(currentProgress + increment, targetProgress);
                return Math.round(newProgress);
            });
        }, 50);

        return () => clearInterval(animationInterval);
    }, [targetProgress, isBulkApproving]);

    useEffect(() => {
        const getTags = async () => {
            const tags = await getAllTags(currentCatalog === "partnercatalog", props.isRetailer);
            if (tags.errorMessage)
                setError(tags);
            else
                setAvalTags(tags);
        }

        const getPartners = async () => {
            let partners = await utilGetPartners();
            if (partners.errorMessage) {
                setError({open: true, errorHeader: "Error", errorMessage: partners.errorMessage});
                return;
            }

            setAvailablePartners(partners);
        }

        // const toggleDefaultToggledColumns = () => {
        //     let toggledColumns = props.config.columns.filter((column) => column.defaultToggled);
        //     toggledColumns.forEach((column) => {
        //         table.extensions.toggleColumn(column, true);
        //     });
        // }

        // Load tags for non-retailers or retailers when bulk edit is disabled
        if (!props.isRetailer || (props.isRetailer && !BULK_EDIT_ENABLED)) {
            getTags();
        }
        getPartners();
        // toggleDefaultToggledColumns();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    //endregion

    return (
        <>
            {/*region Saved Search - only for mycatalog and partnercatalog */}
            {(currentCatalog === "mycatalog" || currentCatalog === "partnercatalog") && (
                <Paper style={{width: "100%", padding: '10px 12px'}}>
                    <SavedSearchManager
                        tableType={currentCatalog === "mycatalog" ? "poc-mycatalog" : "poc-partnercatalog"}
                        tableState={getTableConfig(table, ["syncStatus", "approvalStatus", "errorMessage"], props.config)}
                        onConfigApplied={(config) => {
                            applySavedSearchConfig(config, {
                                table: table,
                                columns: props.config.columns,
                                setConfig: props.setConfig
                            });
                        }}
                    />
                </Paper>
            )}

            {/*endregion*/}
            {/*region Catalog Filters*/}
            <Paper style={{width: "100%"}}>
                <Grid container>
                    <CatalogFilters
                        Filters={
                            <>
                                {(isMarketplace && currentCatalog === "mycatalog" && SUPPLIER_SYNDICATION_ENABLED) ? (
                                        <Grid container style={{gap: "10px", width: "auto", flex: "auto"}}>
                                            <ProductFiltersChannels
                                                table={props.table}
                                                retailers={props.retailers.map(retailer => {
                                                    return {
                                                        ...availablePartners.find(partner => partner.Id === retailer.Id),
                                                        Marketplace: retailer.Marketplace
                                                    };
                                                })}
                                            />

                                            <ProductFiltersSyncStatus
                                                table={props.table}
                                            />

                                            <ProductFiltersApprovalStatus
                                                table={props.table}
                                            />
                                        </Grid>
                                    )
                                    : (
                                        <CatalogProductFilters
                                            currentCatalog={currentCatalog} merchantId={merchantId}
                                            isRetailer={props.isRetailer}
                                            table={props.table} refresh={updating}
                                        />
                                    )
                                }
                            </>
                        }
                        table={props.table}
                        config={props.config} setConfig={props.setConfig}
                        history={props.history} location={props.location}
                        filterShowExceptions={["syncStatus", "approvalStatus", "errorMessage"]}
                    />
                </Grid>
            </Paper>
            {/*endregion*/}
            {!props.isRetailer && currentCatalog === "partnercatalog" && (
                <Paper style={{width: "100%", padding: "1em"}}>
                    <Grid container style={{flexDirection: "column"}} alignItems={"flex-start"}>
                        <Typography style={{fontWeight: "bold"}}>Partner Select</Typography>
                        <EnhancedSelect
                            placeholder={"Partner"}
                            options={availablePartners.map((partner) => ({
                                label: partner.CompanyName,
                                value: partner.Id
                            }))
                            }
                            onChange={(e) => {
                                let merchantId = e ? e.value : "";

                                setSelectedPartnerId(merchantId);
                                new Helpers().setUrlObject('merchantId', merchantId, props.history, props.location);
                                props.setConfig((prevState) => ({
                                    ...prevState,
                                    url: retailerCatalogSearch + `?merchantId=${merchantId}`,
                                    refresh: !props.config.refresh
                                }));
                            }}
                            value={selectedPartnerId === "" ? "" : {
                                // eslint-disable-next-line eqeqeq
                                label: availablePartners.find((partner) => partner.Id == selectedPartnerId)?.CompanyName || selectedPartnerId,
                                value: selectedPartnerId
                            }}
                            containerStyle={{width: "100%"}}
                        />
                    </Grid>
                </Paper>
            )}
            {/*region Table */}
            <Paper style={{width: "100%"}}>
                <Grid container style={{overflow: "auto", flexDirection: "column"}}>
                    <CatalogManagerTable
                        isRetailer={props.isRetailer}
                        table={table}
                        config={props.config}
                        generateRows={generateRows}
                        isSelectable={hasInventoryManagePermission
                            && (currentCatalog === "mycatalog" || (currentCatalog === "partnercatalog" && props.isRetailer))}
                        bulkActions={(
                                (props.isRetailer && hasInventoryManagePermission)
                                || (!props.isRetailer && currentCatalog === "mycatalog" && hasInventoryManagePermission))
                            && (
                            <Grid container style={{ gap: "10px" }}>
                                <ButtonGroup variant="outlined"
                                    style={{
                                        visibility: props.table.state.selected.length > 0 ? "visible" : "hidden",
                                        opacity: props.table.state.selected.length > 0 ? 1 : 0,
                                    }}>

                                    {(((isMarketplace && SUPPLIER_SYNDICATION_ENABLED) || props.isRetailer) && currentCatalog === "mycatalog") && (
                                        <Button onClick={(e) => {
                                            bulkUpdateStatus("Listing Pending");
                                        }}>List</Button>
                                    )}

                                    {(((isMarketplace && SUPPLIER_SYNDICATION_ENABLED) || props.isRetailer) && currentCatalog === "mycatalog") && (
                                        <Button onClick={(e) => {
                                            bulkUpdateStatus("Delist Pending");
                                        }}>Delist</Button>
                                    )}

                                    {currentCatalog === "partnercatalog" && props.isRetailer && (
                                        <Button onClick={(e) => {
                                            bulkApprove(true);
                                        }}>Approve & List</Button>
                                    )}

                                    {currentCatalog === "partnercatalog" && props.isRetailer && (
                                        <Button onClick={(e) => {
                                            bulkApprove(false);
                                        }}>Approve</Button>
                                    )}

                                    {currentCatalog === "partnercatalog" && props.isRetailer && (
                                        <Button onClick={(e) => {
                                            setBulkRejectConfirm(true);
                                            setBulkRejectAnchorEl(e.currentTarget);
                                        }}>Reject</Button>
                                    )}

                                    {currentCatalog === "partnercatalog" && props.isRetailer && (
                                        <Button onClick={(e) => {
                                            setUpdateStatusesOpen(true);
                                            setUpdateStatusesAnchorEl(e.currentTarget);
                                        }}>Update Status</Button>
                                    )}
                                </ButtonGroup>

                                {(!props.isRetailer || (props.isRetailer && !BULK_EDIT_ENABLED)) && (
                                    <ButtonGroup
                                        variant="outlined"
                                        style={{
                                            visibility: props.table.state.selected.length > 0 ? "visible" : "hidden",
                                            opacity: props.table.state.selected.length > 0 ? 1 : 0,
                                        }}
                                    >
                                        <Button onClick={(e) => {
                                            setBulkTagOpen(true);
                                            setBulkTagAnchorEl(e.currentTarget);
                                        }}>
                                            Tag
                                        </Button>

                                        <Button onClick={(e) => {
                                            setBulkUntagOpen(true);
                                            setBulkUntagAnchorEl(e.currentTarget);
                                        }}>
                                            Untag
                                        </Button>
                                    </ButtonGroup>
                                )}

                                <ButtonGroup
                                    variant="outlined"
                                    style={{
                                        visibility: props.table.state.selected.length > 0 ? "visible" : "hidden",
                                        opacity: props.table.state.selected.length > 0 ? 1 : 0,
                                    }}
                                >
                                    {((props.isRetailer || currentCatalog === "mycatalog") && BULK_EDIT_ENABLED) && (
                                        <Tooltip
                                            title={bulkEditSpansMultipleMerchants
                                                ? "Bulk edit is only available for products from a single partner. Narrow your selection to one partner before editing."
                                                : ""}
                                        >
                                            <span>
                                                <Button
                                                    disabled={bulkEditSpansMultipleMerchants}
                                                    onClick={(e) => {
                                                        setBulkEditOpen(true);
                                                    }}
                                                >
                                                    Edit
                                                </Button>
                                            </span>
                                        </Tooltip>
                                    )}

                                    <Button onClick={(e) => {
                                        setBulkDeleteConfirm(true);
                                    }}>
                                        Delete
                                    </Button>
                                </ButtonGroup>
                            </Grid>
                            )}
                        tableMessage={
                            (!props.isRetailer && !merchantId && currentCatalog === "partnercatalog") // Supplier Catalog no partner selected
                                ? (
                                    <TableCell colSpan={props.config.columns.length}>
                                        <Typography style={{textAlign: "center", color: "#4F4D55", margin: "1em 0"}}>
                                            <b>Please select a partner to view their catalog</b>
                                        </Typography>
                                    </TableCell>
                                )
                                : (table.data.length === 0) // No table data
                                    ? (
                                    <TableCell colSpan={props.config.columns.length}>
                                        <Typography style={{textAlign: "center", color: "#4F4D55", margin: "1em 0"}}>
                                            <b>No catalog data found</b>
                                        </Typography>
                                    </TableCell>
                                ) : undefined
                        }
                    />
                </Grid>
            </Paper>
            {/*endregion*/}
            {/*region Dialogs & Actions*/}
            <EnhancedConfirmDialog open={bulkDeleteConfirm} setOpen={setBulkDeleteConfirm}
                                   action={(action) => {
                                       if (action) bulkDelete()
                                   }} title={"Delete Products?"}
                                   message={"This action cannot be undone, please ensure you have delisted any products before deleting!"}
                                   type={"delete"}
            />
            <BulkApprovalActionsTable
                isRetailer={props.isRetailer}
                open={bulkApproveManualReviewsOpen} setOpen={setBulkApproveManualReviewsOpen}
                setUpdating={setUpdating} setError={setError}
                manualApproveProducts={bulkApproveManualReviews} currentCatalog={currentCatalog}
                approveAndList={bulkApproveManualReviewApproveAndList}
                forceUniqueRetailerSku={props.forceUniqueRetailerSku}
                onClose={() => {
                    props.setConfig((prevState) => ({...prevState, refresh: !props.config.refresh}));
                }}
                onFinish={() => {
                    props.setConfig((prevState) => ({...prevState, refresh: !props.config.refresh}));
                }}
            />
            <ProductApproveDialog
                isRetailer={props.isRetailer}
                setUpdating={setUpdating} setError={setError}
                open={approveDialogOpen} setOpen={setApproveDialogOpen}
                product={selectedRow} currentCatalog={currentCatalog}
                forceUniqueRetailerSku={props.forceUniqueRetailerSku}
                onApprove={() => {
                    props.setConfig((prevState) => ({...prevState, refresh: !props.config.refresh}));
                }}
            />
            <ProductDeleteDialog
                isRetailer={props.isRetailer}
                setUpdating={setUpdating} setError={setError}
                open={deleteDialogOpen} setOpen={setDeleteDialogOpen}
                product={selectedRow} currentCatalog={currentCatalog}
                onDelete={() => {
                    props.setConfig((prevState) => ({...prevState, refresh: !props.config.refresh}));
                }}
            />
            <ProductRejectDialog
                isRetailer={props.isRetailer}
                setUpdating={setUpdating} setError={setError}
                open={rejectDialogOpen} setOpen={setRejectDialogOpen}
                product={selectedRow} currentCatalog={currentCatalog}
                onReject={() => {
                    props.setConfig((prevState) => ({...prevState, refresh: !props.config.refresh}));
                }}
            />
            <UpdatingDialog
                open={updating}
                progress={isBulkApproving ? updatingProgress : undefined}
                title={isBulkApproving ? "Approving Products..." : undefined}
                subtitle={isBulkApproving ? "Please wait while we approve the selected products." : undefined}
            />
            <EnhancedErrorDialog open={error.open} onClose={() => setError({...error, open: false})}
                                 errorHeader={error.errorHeader} errorMessage={error.errorMessage}/>
            <BulkEditActions
                open={bulkEditOpen}
                setOpen={setBulkEditOpen}
                selectedProducts={table.data.filter((product) => props.table.state.selected.includes(product.id))}
                isRetailer={props.isRetailer}
                currentCatalog={currentCatalog}
                setUpdating={setUpdating}
                setError={setError}
                onComplete={() => {
                    props.setConfig((prevState) => ({...prevState, refresh: !props.config.refresh}));
                }}
            />
            {/*endregion*/}
            {/*region Bulk Action Poppers */}
            {(!props.isRetailer || (props.isRetailer && !BULK_EDIT_ENABLED)) && (
                <>
                    <Popper open={bulkTagOpen} anchorEl={bulkTagAnchorEl} transition
                            placement={"bottom-start"}>
                        {({TransitionProps}) => (
                            <ClickAwayListener onClickAway={() => {
                                setBulkTagOpen(false);
                                setBulkTagAnchorEl(null)
                            }}>
                                <Fade {...TransitionProps} timeout={350}>
                                    <BulkUpdateStatus container>
                                        <Typography>Bulk add tags to the selected products</Typography>

                                        <EnhancedAutocomplete value={bulkTagTags}
                                                              options={avalTags.filter((tag) => !bulkTagTags.includes(tag))}
                                                              onChange={(e, value) => setBulkTagTags(value)}/>

                                        <Grid item container style={{width: "100%", gap: "8px"}} justifyContent={"flex-end"}>
                                            <Button
                                                    style={{padding: "6px 12px"}}
                                                    onClick={() => {
                                                        setBulkTagOpen(false);
                                                        setBulkTagAnchorEl(null);
                                                        setBulkTagTags([]);
                                                    }}
                                            >
                                                Cancel
                                            </Button>
                                            <Button variant="outlined" color="primary"
                                                style={{padding: "6px 12px"}}
                                                disabled={bulkTagTags.length === 0}
                                                onClick={() => {
                                                    bulkTag();
                                                    setBulkTagOpen(false);
                                                    setBulkTagAnchorEl(null);
                                                    setBulkTagTags([]);
                                                }}
                                            >
                                                Save
                                            </Button>
                                        </Grid>
                                    </BulkUpdateStatus>
                                </Fade>
                            </ClickAwayListener>
                        )}
                    </Popper>

                    <Popper open={bulkUntagOpen} anchorEl={bulkUntagAnchorEl} transition
                            placement={"bottom-start"}>
                        {({TransitionProps}) => (
                            <ClickAwayListener onClickAway={() => {
                                setBulkUntagOpen(false);
                                setBulkUntagAnchorEl(null)
                            }}>
                                <Fade {...TransitionProps} timeout={350}>
                                    <BulkUpdateStatus container>
                                        <Typography>Bulk remove tags from the selected products</Typography>

                                        <EnhancedAutocomplete value={bulkUntagTags}
                                                              options={avalTags.filter((tag) => !bulkUntagTags.includes(tag))}
                                                              onChange={(e, value) => setBulkUntagTags(value)}/>

                                        <Grid item container style={{width: "100%", gap: "8px"}} justifyContent={"flex-end"}>
                                            <Button
                                                    style={{padding: "6px 12px"}}
                                                    onClick={() => {
                                                        setBulkUntagOpen(false);
                                                        setBulkUntagAnchorEl(null);
                                                        setBulkUntagTags([]);
                                                    }}
                                            >
                                                Cancel
                                            </Button>
                                            <Button variant="outlined" color="primary"
                                                style={{padding: "6px 12px"}}
                                                disabled={bulkUntagTags.length === 0}
                                                onClick={() => {
                                                    bulkUntag();
                                                    setBulkUntagOpen(false);
                                                    setBulkUntagAnchorEl(null);
                                                    setBulkUntagTags([]);
                                                }}
                                            >
                                                Save
                                            </Button>
                                        </Grid>
                                    </BulkUpdateStatus>
                                </Fade>
                            </ClickAwayListener>
                        )}
                    </Popper>
                </>
            )}
            {currentCatalog === "partnercatalog" && (
                <Popper open={updateStatusesOpen} anchorEl={updateStatusesAnchorEl} transition
                        placement={"bottom-start"}>
                    {({TransitionProps}) => (
                        <ClickAwayListener onClickAway={() => {
                            setUpdateStatusesOpen(false);
                            setUpdateStatusesAnchorEl(null)
                        }}>
                            <Fade {...TransitionProps} timeout={350}>
                                <BulkUpdateStatus container>
                                    <Typography>Update Status for Products you selected</Typography>

                                    <EnhancedSelect
                                        value={updateStatusValue}
                                        onChange={setUpdateStatusValue}
                                        placeholder={"Approval Status"}
                                        options={[
                                            {label: "Ignored", value: "Ignored"},
                                            {label: "Flagged", value: "Flagged"},
                                            {label: "Under Review", value: "Under Review"},
                                            {label: "Update Suggested", value: "Update Suggested"},
                                        ]}
                                    />

                                    <Grid item container style={{width: "100%", gap: "8px"}} justifyContent={"flex-end"}>
                                        <Button
                                                style={{padding: "6px 12px"}}
                                                onClick={() => {
                                                    setUpdateStatusesOpen(false);
                                                    setUpdateStatusesAnchorEl(null);
                                                }}
                                        >
                                            Cancel
                                        </Button>
                                        <Button variant="outlined" color="primary"
                                            style={{padding: "6px 12px"}}
                                            onClick={() => {
                                                if (updateStatusValue == null) return;
                                                bulkUpdateStatus(updateStatusValue.value);
                                                setUpdateStatusesOpen(false);
                                                setUpdateStatusesAnchorEl(null);
                                            }}
                                        >
                                            Save
                                        </Button>
                                    </Grid>
                                </BulkUpdateStatus>
                            </Fade>
                        </ClickAwayListener>
                    )}
                </Popper>
            )}
            {currentCatalog === "partnercatalog" && (
                <Popper open={bulkRejectConfirm} anchorEl={bulkRejectAnchorEl} transition
                        placement={"bottom-start"}>
                    {({TransitionProps}) => (
                        <ClickAwayListener onClickAway={() => {
                            setBulkRejectConfirm(false);
                            setBulkRejectAnchorEl(null)
                        }}>
                            <Fade {...TransitionProps} timeout={350}>
                                <BulkUpdateStatus container>
                                    <Typography>Do you want to add a rejection note?</Typography>

                                    <EnhancedTextField placeholder={"Optional Note..."} value={rejectOptionalNote}
                                                       onChange={(e) => setRejectOptionalNote(e.target.value)}
                                                       multiline rows={4} multilinestyles={{padding: "10px"}}
                                                       inputstyles={{padding: 0}}/>

                                    <Grid item container style={{width: "100%", gap: "8px"}} justifyContent={"flex-end"}>
                                        <Button
                                                style={{padding: "6px 12px"}}
                                                onClick={() => {
                                                    setBulkRejectConfirm(false);
                                                    setBulkRejectAnchorEl(null);
                                                }}
                                        >
                                            Cancel
                                        </Button>
                                        <Button variant="outlined" color="primary"
                                            style={{padding: "6px 12px"}}
                                            onClick={() => {
                                                bulkUpdateStatus("Not Compliant");
                                                setBulkRejectConfirm(false);
                                                setBulkRejectAnchorEl(null);
                                            }}
                                        >
                                            Save
                                        </Button>
                                    </Grid>
                                </BulkUpdateStatus>
                            </Fade>
                        </ClickAwayListener>
                    )}
                </Popper>
            )}
            {/*endregion*/}
        </>
    );
}

export default CatalogResults;
